/**
 * プロジェクトリポジトリ
 * プロジェクト、工区、日次活動、割り当ての管理
 */

import apiClient from '@/shared/api/client'
import { Project, ProjectResponse, ProjectWithZones, ProjectZone, ProjectInsert, ProjectUpdate } from '@/features/projects/types'
import { DailyActivity, DailyActivityInsert, DailyActivityUpdate } from '@/features/projects/types'
import { CraftsmanAssignment, CraftsmanAssignmentInsert, CraftsmanAssignmentUpdate, TruckAssignment, TruckAssignmentInsert, TruckAssignmentUpdate } from '@/features/projects/types'
import { mastersApi } from '@/features/masters/api'

/**
 * プロジェクトリポジトリ
 * バックエンドAPI経由でプロジェクト、工区、日次活動、割り当ての管理を行う
 */
export class ProjectRepository {

  /**
   * プロジェクト一覧を取得
   */
  async getProjects(params: {
    tenant_id?: string;
    status?: string | string[];
    building_type?: string | string[];
    name?: string;
    start_date_from?: string;
    start_date_to?: string;
    end_date_from?: string;
    end_date_to?: string;
    area_min?: number;
    area_max?: number;
    limit?: number;
    skip?: number;
    include_zones?: boolean;
  } = {}): Promise<{ items: ProjectWithZones[]; total: number }> {
    try {
      const response = await apiClient.get<{ items: ProjectWithZones[]; total: number }>('projects', {
        params: {
          ...params,
          include_zones: params.include_zones ?? true
        }
      })

      return {
        items: response.data.items,
        total: response.data.total ?? response.data.items.length,
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      throw error;
    }
  }

  /**
   * プロジェクト詳細を取得
   */
  async getProject(projectId: string): Promise<ProjectWithZones> {
    try {
      const response = await apiClient.get<ProjectWithZones>(`projects/${projectId}`, {
        params: { include_zones: true }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクトを作成
   */
  async createProject(data: ProjectInsert): Promise<ProjectResponse> {
    try {
      const response = await apiClient.post<ProjectResponse>('projects', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    }
  }

  /**
   * プロジェクトを更新
   */
  async updateProject(projectId: string, data: ProjectUpdate): Promise<Project> {
    try {
      const response = await apiClient.put<Project>(`projects/${projectId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクトを削除（論理削除）
   */
  async deleteProject(projectId: string): Promise<void> {
    try {
      await apiClient.delete(`projects/${projectId}`);
    } catch (error) {
      console.error(`Failed to delete project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクト工区一覧を取得
   */
  async getProjectZones(projectId: string): Promise<ProjectZone[]> {
    try {
      const response = await apiClient.get<{ items: ProjectZone[]; total: number }>(`zones/project/${projectId}`);
      return response.data.items;
    } catch (error) {
      console.error(`Failed to fetch zones for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクト工区を作成
   */
  async createProjectZone(projectId: string, data: Omit<ProjectZone, 'id' | 'project_id' | 'created_at' | 'updated_at'>): Promise<ProjectZone> {
    try {
      const response = await apiClient.post<ProjectZone>('zones', {
        ...data,
        project_id: projectId
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to create zone for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクト工区を更新
   */
  async updateProjectZone(zoneId: string, data: Partial<ProjectZone>): Promise<ProjectZone> {
    try {
      const response = await apiClient.put<ProjectZone>(`zones/${zoneId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update zone ${zoneId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクト工区を削除
   */
  async deleteProjectZone(zoneId: string): Promise<void> {
    try {
      await apiClient.delete(`zones/${zoneId}`);
    } catch (error) {
      console.error(`Failed to delete zone ${zoneId}:`, error);
      throw error;
    }
  }

  /**
   * 日次活動一覧を取得
   */
  async getDailyActivities(projectId: string, params: {
    start_date: string;
    end_date: string;
    include_assignments?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<DailyActivity[]> {
    try {
      const response = await apiClient.get<{ items: DailyActivity[]; total: number }>(
        `projects/${projectId}/daily-activities`,
        { params }
      );
      return response.data.items;
    } catch (error) {
      console.error(`Failed to fetch daily activities for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * 日次活動を作成
   */
  async createDailyActivity(data: DailyActivityInsert): Promise<DailyActivity> {
    try {
      const response = await apiClient.post<DailyActivity>('daily-activities', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create daily activity:', error);
      throw error;
    }
  }

  /**
   * 日次活動を更新
   */
  async updateDailyActivity(activityId: string, data: DailyActivityUpdate): Promise<DailyActivity> {
    try {
      const response = await apiClient.put<DailyActivity>(`daily-activities/${activityId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update daily activity ${activityId}:`, error);
      throw error;
    }
  }

  /**
   * 日次活動を削除
   */
  async deleteDailyActivity(activityId: string): Promise<void> {
    try {
      await apiClient.delete(`daily-activities/${activityId}`);
    } catch (error) {
      console.error(`Failed to delete daily activity ${activityId}:`, error);
      throw error;
    }
  }

  /**
   * 職人割り当てを作成
   */
  async createCraftsmanAssignment(data: CraftsmanAssignmentInsert): Promise<CraftsmanAssignment> {
    try {
      const response = await apiClient.post<CraftsmanAssignment>('craftsman-assignments', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create craftsman assignment:', error);
      throw error;
    }
  }

  /**
   * 職人割り当てを更新
   */
  async updateCraftsmanAssignment(assignmentId: string, data: CraftsmanAssignmentUpdate): Promise<CraftsmanAssignment> {
    try {
      const response = await apiClient.patch<CraftsmanAssignment>(`craftsman-assignments/${assignmentId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update craftsman assignment ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * 職人割り当てを削除
   */
  async deleteCraftsmanAssignment(assignmentId: string): Promise<void> {
    try {
      await apiClient.delete(`craftsman-assignments/${assignmentId}`);
    } catch (error) {
      console.error(`Failed to delete craftsman assignment ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * トラック割り当てを作成
   */
  async createTruckAssignment(data: TruckAssignmentInsert): Promise<TruckAssignment> {
    try {
      const response = await apiClient.post<TruckAssignment>('truck-assignments', data);
      return response.data;
    } catch (error) {
      console.error('Failed to create truck assignment:', error);
      throw error;
    }
  }

  /**
   * トラック割り当てを更新
   */
  async updateTruckAssignment(assignmentId: string, data: TruckAssignmentUpdate): Promise<TruckAssignment> {
    try {
      const response = await apiClient.patch<TruckAssignment>(`truck-assignments/${assignmentId}`, data);
      return response.data;
    } catch (error) {
      console.error(`Failed to update truck assignment ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * トラック割り当てを削除
   */
  async deleteTruckAssignment(assignmentId: string): Promise<void> {
    try {
      await apiClient.delete(`truck-assignments/${assignmentId}`);
    } catch (error) {
      console.error(`Failed to delete truck assignment ${assignmentId}:`, error);
      throw error;
    }
  }

  /**
   * プロジェクト統計を取得
   */
  async getProjectStats(): Promise<{
    total: number;
    in_progress: number;
    completed: number;
    on_hold: number;
    archived: number;
  }> {
    try {
      const response = await apiClient.get<{
        total: number;
        in_progress: number;
        completed: number;
        on_hold: number;
        archived: number;
      }>('projects/stats');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch project stats:', error);
      throw error;
    }
  }
}

// シングルトンインスタンスをエクスポート
export const projectRepository = new ProjectRepository();
